import { useState } from 'react'
import type { DiscountWindowType } from '@/lib/api/discount-types'
import { timeZoneLabel } from '@/lib/eventTime'
import { cn } from '@/lib/utils'
import { FieldError, Notice, Section } from './editor-fields'
import { toggleId, type DraftUpdate, type ScheduleDraft } from './editor-model'
import { HINT, LABEL, fieldClass, segmentClass } from './editor-styles'
import { DAYS } from './event-clock'
import { formatDateSpan, type EventDatesFit } from './list-format'

type WindowUpdate = DraftUpdate<'windowType' | 'startsAt' | 'endsAt' | 'days' | 'dailyStart' | 'dailyEnd' | 'startDate' | 'endDate'>

const WINDOW_TYPES: { value: DiscountWindowType; label: string }[] = [
  { value: 'one_off', label: 'One-off' },
  { value: 'recurring', label: 'Recurring' },
]

function ClearButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="text-xs font-medium text-blue-600 hover:underline disabled:hidden dark:text-blue-400"
    >
      Clear
    </button>
  )
}

function oneOffHint(draft: ScheduleDraft): string {
  if (!draft.startsAt && !draft.endsAt) return 'No start and no end: it applies whenever the schedule is active.'
  if (!draft.startsAt) return 'No start: it applies from now until the end.'
  if (!draft.endsAt) return 'No end: it runs from the start until you pause or archive it.'
  return 'Either can be left blank for an open-ended window.'
}

function midnightHint(start: string, end: string): string | null {
  if (!start || !end) return null
  if (end === start) return 'The start and end are the same, so each window runs a full 24 hours.'
  if (end < start) return `The end is before the start, so each window runs past midnight and ends at ${end} the next day.`
  return null
}

export function WindowSection({ draft, onChange, timeZone, eventDates, errors }: {
  draft: ScheduleDraft
  onChange: (update: WindowUpdate) => void
  timeZone: string
  /** How the drafted window sits against the event's dates; null when there is nothing to judge. */
  eventDates: EventDatesFit | null
  errors: { window?: string | null; endsAt?: string | null; days?: string | null; dailyTimes?: string | null; endDate?: string | null }
}) {
  const zone = timeZoneLabel(timeZone)
  const toggleDay = (day: number) => onChange((current) => ({ days: toggleId(current.days, day).sort((a, b) => a - b) }))
  const crossing = midnightHint(draft.dailyStart, draft.dailyEnd)
  // Only for the year: dates outside the current one carry it.
  const [openedAt] = useState(() => new Date())
  const eventSpan = eventDates && `${formatDateSpan(eventDates.startDate, eventDates.endDate, timeZone, openedAt)}, ${zone}`

  return (
    <Section
      title="When"
      description={`Dates and times are the event's local time (${zone}).`}
      aside={
        <div role="group" aria-label="Window type" className="inline-flex">
          {WINDOW_TYPES.map((type) => (
            <button
              key={type.value}
              type="button"
              aria-pressed={draft.windowType === type.value}
              onClick={() => onChange({ windowType: type.value })}
              className={segmentClass(draft.windowType === type.value)}
            >
              {type.label}
            </button>
          ))}
        </div>
      }
    >
      {errors.window && <FieldError>{errors.window}</FieldError>}
      {draft.windowType === 'one_off' ? (
        <div className="space-y-2">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <div className="flex items-baseline justify-between gap-2">
                <label htmlFor="discount-starts-at" className={LABEL}>Starts</label>
                {draft.startsAt && <ClearButton label="Clear the start" onClick={() => onChange({ startsAt: '' })} />}
              </div>
              <input
                id="discount-starts-at"
                type="datetime-local"
                value={draft.startsAt}
                onChange={(e) => onChange({ startsAt: e.target.value })}
                className={fieldClass(false, 'mt-1 w-full')}
              />
            </div>
            <div>
              <div className="flex items-baseline justify-between gap-2">
                <label htmlFor="discount-ends-at" className={LABEL}>Ends</label>
                {draft.endsAt && <ClearButton label="Clear the end" onClick={() => onChange({ endsAt: '' })} />}
              </div>
              <input
                id="discount-ends-at"
                type="datetime-local"
                value={draft.endsAt}
                min={draft.startsAt || undefined}
                onChange={(e) => onChange({ endsAt: e.target.value })}
                className={fieldClass(!!errors.endsAt, 'mt-1 w-full')}
              />
              <FieldError>{errors.endsAt}</FieldError>
            </div>
          </div>
          <p className={HINT}>{oneOffHint(draft)} Times are {zone}.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <span className={LABEL}>Days</span>
            <div role="group" aria-label="Days of the week" className="mt-1 flex flex-wrap gap-1.5">
              {DAYS.map((day) => {
                const selected = draft.days.includes(day.value)
                return (
                  <button
                    key={day.value}
                    type="button"
                    aria-pressed={selected}
                    aria-label={day.long}
                    onClick={() => toggleDay(day.value)}
                    className={cn(
                      'min-w-[3.25rem] rounded-full border px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed',
                      selected
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-300 dark:hover:bg-neutral-800',
                    )}
                  >
                    {day.short}
                  </button>
                )
              })}
            </div>
            <p className={cn(HINT, 'mt-1')}>
              {draft.days.length === 0 ? 'No day chosen: it runs every day.' : draft.days.length === 7 ? 'Every day.' : 'Only on the chosen days (by the day the window starts).'}
            </p>
            <FieldError>{errors.days}</FieldError>
          </div>

          <div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="discount-daily-start" className={LABEL}>Daily start</label>
                <input
                  id="discount-daily-start"
                  type="time"
                  value={draft.dailyStart}
                  onChange={(e) => onChange({ dailyStart: e.target.value })}
                  className={fieldClass(!!errors.dailyTimes && !draft.dailyStart, 'mt-1 w-full')}
                />
              </div>
              <div>
                <label htmlFor="discount-daily-end" className={LABEL}>Daily end</label>
                <input
                  id="discount-daily-end"
                  type="time"
                  value={draft.dailyEnd}
                  onChange={(e) => onChange({ dailyEnd: e.target.value })}
                  className={fieldClass(!!errors.dailyTimes && !draft.dailyEnd, 'mt-1 w-full')}
                />
              </div>
            </div>
            <FieldError>{errors.dailyTimes}</FieldError>
            {crossing && <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">{crossing}</p>}
          </div>

          <div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-baseline justify-between gap-2">
                  <label htmlFor="discount-start-date" className={LABEL}>First date</label>
                  {draft.startDate && <ClearButton label="Clear the first date" onClick={() => onChange({ startDate: '' })} />}
                </div>
                <input
                  id="discount-start-date"
                  type="date"
                  value={draft.startDate}
                  onChange={(e) => onChange({ startDate: e.target.value })}
                  className={fieldClass(false, 'mt-1 w-full')}
                />
              </div>
              <div>
                <div className="flex items-baseline justify-between gap-2">
                  <label htmlFor="discount-end-date" className={LABEL}>Last date</label>
                  {draft.endDate && <ClearButton label="Clear the last date" onClick={() => onChange({ endDate: '' })} />}
                </div>
                <input
                  id="discount-end-date"
                  type="date"
                  value={draft.endDate}
                  min={draft.startDate || undefined}
                  onChange={(e) => onChange({ endDate: e.target.value })}
                  className={fieldClass(!!errors.endDate, 'mt-1 w-full')}
                />
              </div>
            </div>
            <FieldError>{errors.endDate}</FieldError>
            <p className={cn(HINT, 'mt-1')}>Optional. Leave blank to repeat with no first or last date.</p>
          </div>
        </div>
      )}
      {/* Prices are charged only inside the event's dates; saving is still allowed. */}
      {eventDates?.overlap === 'none' && (
        <Notice kind="warning">This window is outside the event's dates ({eventSpan}), so it won't discount anything.</Notice>
      )}
      {eventDates?.overlap === 'partial' && eventDates.boundBeyondEvent && (
        <Notice kind="info">Only the part during the event's dates ({eventSpan}) is discounted.</Notice>
      )}
    </Section>
  )
}
